import { tokens } from '@cast/design-system';

import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { ResourceBreakdownChart } from '../_components/ResourceBreakdownChart';
import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';

import { useGpuUtilizationBreakdownChartData } from './_hooks/useGpuUtilizationBreakdownChartData';
import { GpuUtilizationBreakdownChartDataKey } from './_types';
import { isGpuDateRangeSupported } from './_utils/isGpuDateRangeSupported';

const GPU_UTILIZATION_BREAKDOWN_CHART_DATA_SETTINGS: Record<
  GpuUtilizationBreakdownChartDataKey,
  {
    color: string;
    title: string;
    tooltipTitle: string;
  }
> = {
  gpuUtilization: {
    color: tokens.primitives.colors.aqua['600'],
    title: 'AVG. GPU UTILIZATION',
    tooltipTitle: 'GPU utilization',
  },
  gpuMemoryUtilization: {
    color: tokens.primitives.colors.yellow['900'],
    title: 'AVG. GPU MEMORY UTILIZATION',
    tooltipTitle: 'GPU memory utilization',
  },
};

const DATA_KEYS: GpuUtilizationBreakdownChartDataKey[] = [
  'gpuUtilization',
  'gpuMemoryUtilization',
];

const TITLE = 'GPU utilization';
const TEST_ID = 'gpu-utilization-breakdown-chart';

export const GpuUtilizationBreakdownChart = () => {
  const { timeZone, getRange } = useInventoryWorkloadsTimeRangeSelection();
  const { from, to } = getRange();

  const { data, isLoading, isFetching, error, refetch, timeUnit, step } =
    useGpuUtilizationBreakdownChartData();

  if (!isGpuDateRangeSupported(from, to)) {
    return (
      <WidgetLayout title={TITLE} testId={TEST_ID}>
        <DateRangeUnsupportedBanner testId={`${TEST_ID}-unsupported-range`} />
      </WidgetLayout>
    );
  }

  return (
    <ResourceBreakdownChart
      title={TITLE}
      testId={TEST_ID}
      data={data}
      isLoading={isLoading}
      isFetching={isFetching}
      error={error}
      refetch={refetch}
      dataKeys={DATA_KEYS}
      chartDataSettings={GPU_UTILIZATION_BREAKDOWN_CHART_DATA_SETTINGS}
      unit="%"
      timeZone={timeZone}
      dateRange={{ from, to }}
      timeUnit={timeUnit}
      step={step}
      filler={() => ({})}
    />
  );
};
